import { useEffect, useMemo } from 'react';
import { Button } from '@mui/material';
import { LedgerDialog } from 'components/Dialogs';
import ScalarSpreadsheet from 'components/ScalarSpreadsheet';
import useWorkbook from 'components/ScalarSpreadsheet/utilities/useWorkbook';
import { useCapTableContext } from 'context/CapTableContext';
import { getStringValue, gridShortDate } from 'utilities';
import useDividendRateLedger from './config/useDividendRateLedger';
import useDividendRateLedgerActions from './config/useDividendRateLedgerActions';
import { DividendRateLedgerProps } from './DividendRateLedger.props';

const DividendRateLedger = (props: DividendRateLedgerProps) => {
  const { closeDialog, cell, tableData, onInit } = props;
  const { divRateLedgerItemAttrs, measurementDate } = useCapTableContext();

  const cellColumnRef = useMemo(() => cell.columnRef, [cell.columnRef]);
  const cellColumnLegend = useMemo(() => getStringValue(cell.columnLegend), [cell.columnLegend]);

  const startingColumns = useMemo(() => tableData?.columns ?? [], [tableData]);
  const currentSecurity = useMemo(
    () => startingColumns.find(column => column.columnRef === cellColumnRef),
    [startingColumns, cellColumnRef]
  );
  const startingDivRate = useMemo(
    () => ({
      id: null,
      name: 'Rate 1',
      effective_date: gridShortDate(currentSecurity?.investment_date),
      dividend_rate: '0',
      has_compounding: false,
      compounding_periods: null,
    }),
    [currentSecurity]
  );

  const ledgerData = useMemo(() => currentSecurity?.dividend_rate_ledger_items ?? [], [currentSecurity]);

  // set origin cell as read only to deal with focus issue
  useEffect(() => {
    onInit?.();
  }, [onInit]);

  const { spreadsheet, setColumns, columns } = useDividendRateLedger({
    ledgerData: ledgerData.length ? ledgerData : [startingDivRate],
    isDisabled: false,
    cellColumnRef: getStringValue(cellColumnRef),
    securities: startingColumns,
    divRateLedgerItemAttrs,
    measurementDate: getStringValue(measurementDate),
  });

  const memoizedSheetList = useMemo(() => [spreadsheet], [spreadsheet]);
  const { workbook, onChange: onWorkbookChange } = useWorkbook(memoizedSheetList);

  const { addColumn, deleteColumn, saveLedger } = useDividendRateLedgerActions({
    columns,
    setColumns,
    sheetsList: memoizedSheetList,
    cellColumnLegend,
    closeDialog,
  });

  return (
    <LedgerDialog
      id={getStringValue(cellColumnRef)}
      onClose={closeDialog}
      title="Multiple Dividend Rates Ledger"
      onSave={saveLedger}>
      {workbook && (
        <ScalarSpreadsheet
          {...memoizedSheetList[0]}
          sheet={memoizedSheetList[0] as object}
          workbook={workbook}
          isLedgerTable
          onChange={onWorkbookChange}
          data={memoizedSheetList[0]?.data ?? []}
          deleteColumn={deleteColumn}
          allowDeleteColumn={columns.length > 1}
        />
      )}
      <br />
      <Button onClick={addColumn}>Add Dividend Rate</Button>
    </LedgerDialog>
  );
};

export default DividendRateLedger;
